import * as React from 'react';
import { cn } from '@/lib/utils';
import { premiumTheme } from './tokens';

/**
 * PremiumPageHeader \u2014 Hero header for screens (Stripe/Notion style).
 *
 * Left: breadcrumb eyebrow + title (18px H1) + subtitle + tags row.
 * Right: action slot (buttons, menu).
 *
 * Per \u00a723.1 the H1 exception allows 18px; all other headings stay \u226414px.
 */

export interface PremiumPageHeaderProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'title'> {
  /** Eyebrow text shown above the title */
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Inline tags rendered under the subtitle */
  tags?: React.ReactNode;
  /** Trailing actions (buttons, menu) */
  actions?: React.ReactNode;
  /** Optional leading avatar or icon */
  leading?: React.ReactNode;
  /** Put the header inside a SectionCard-style container */
  contained?: boolean;
}

export const PremiumPageHeader = React.forwardRef<
  HTMLElement,
  PremiumPageHeaderProps
>(
  (
    {
      eyebrow,
      title,
      subtitle,
      tags,
      actions,
      leading,
      contained = false,
      className,
      ...props
    },
    ref,
  ) => (
    <header
      ref={ref}
      className={cn(
        'flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4',
        contained &&
          'bg-surface-premium border border-border-hairline rounded-xl p-4 md:p-5',
        className,
      )}
      {...props}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        {leading && <div className="flex-shrink-0">{leading}</div>}
        <div className="min-w-0 flex-1">
          {eyebrow && (
            <div
              className={cn(
                premiumTheme.typography.label,
                'mb-1 text-muted-foreground',
              )}
            >
              {eyebrow}
            </div>
          )}
          <h1 className={cn(premiumTheme.typography.h1, 'truncate')}>
            {title}
          </h1>
          {subtitle && (
            <p className={cn(premiumTheme.typography.subtitle, 'mt-1')}>
              {subtitle}
            </p>
          )}
          {tags && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {tags}
            </div>
          )}
        </div>
      </div>
      {actions && (
        <div className="flex items-center gap-2 flex-shrink-0">{actions}</div>
      )}
    </header>
  ),
);
PremiumPageHeader.displayName = 'PremiumPageHeader';
